import React, { useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSavedProperties } from "../hooks/saved/useSavedProperties";
import { useNavigate } from "react-router-dom";
import { useAuthUser, useAuthLoading, useAuthActions } from "@/hooks/auth/useAuth";
import { useTenant } from "../contexts/TenantContext";
import {
  LogOut,
  Rocket,
  Loader,
  RefreshCw,
  UserCircle,
  Mail,
  Phone,
} from "lucide-react";
// import { SavedSearchSection } from "../components/features/dashboard/SavedSearchSection"; // Commented out as requested
import { SavedPropertiesGrid } from "../components/features/dashboard/SavedPropertiesGrid";
import { formatPhone } from "../utils/formatPhone";

export const DashboardPage = () => {
  const user = useAuthUser();
  const { logout } = useAuthActions();
  const authLoading = useAuthLoading();
  const { config, isLoading: configLoading } = useTenant();

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isRefetching } = useSavedProperties();
  const hasRefreshed = useRef(false);

  // Comment out refresh to test if claimedLead is available without refresh
  // Refresh user profile when dashboard is loaded (only once)
  // if (user && !authLoading && !hasRefreshed.current) {
  //   hasRefreshed.current = true;
  //   refreshUserProfile().catch((error) => {
  //     console.error("Error refreshing user profile:", error);
  //   });
  // }

  // Show loading state while auth or config are loading
  if (authLoading || configLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show nothing if not authenticated
  if (!user) {
    return null;
  }

  const handleLogout = async () => {
    await logout();
    navigate("/home");
  };

  return (
    <div className="px-4 py-8 lg:py-20  lg:px-12">
      <div className="max-w-[1300px] 3xl:max-w-[1500px] mx-auto ">
        {/* Welcome Section */}
        <div className="flex flex-col lg:flex-row justify-between items-start mb-8 gap-3">
          <h1 className="text-xl lg:text-3xl font-bold text-gray-900">
            Welcome back,{" "}
            {user.firstName && user.lastName
              ? `${user.firstName} ${user.lastName}`
              : user.firstName || user.lastName || user.email}
          </h1>
          <button
            onClick={handleLogout}
            className="border-gray-500 flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray rounded-lg hover:bg-gray-600 transition-colors !bg-transparent"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>

        {/* Agent Information Section - Only show if user has a claimedLead */}
        {user.claimedLead && (
          <div className="mb-8 p-6 rounded-xl bg-gray-50 border border-gray-200 relative overflow-hidden shadow-md">
              {/* Background pattern */}
              <div
                className="absolute inset-0 opacity-5"
                style={{
                  backgroundImage: `radial-gradient(${
                    config?.branding?.colors?.primary || "#4F46E5"
                  } 1px, transparent 1px)`,
                  backgroundSize: "24px 24px",
                }}
              />

              {/* Content */}
              <div className="relative flex flex-col gap-4">
                {/* Title row */}
                <div>
                  <h3 className="text-xl font-semibold">
                    Your Dedicated Agent
                  </h3>
                  <p className="text-gray-600 text-base">
                    Have questions? Need to request a tour? Contact your agent
                    for any house-related inquiries.
                  </p>
                </div>

                {/* Agent info row */}
                <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 p-2 rounded-full bg-gray-100">
                      {user.claimedLead.photo_url ? (
                        <img
                          src={user.claimedLead.photo_url}
                          alt={`${user.claimedLead.first_name} ${user.claimedLead.last_name}`}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <UserCircle
                          className="w-12 h-12 text-gray-500"
                          style={{ color: config?.branding?.colors?.primary }}
                        />
                      )}
                    </div>
                    <div>
                      <p className="text-lg font-semibold">
                        {`${user.claimedLead.first_name ?? ""} ${
                          user.claimedLead.last_name ?? ""
                        }`.trim()}
                      </p>
                      <div className="flex flex-col sm:flex-row sm:gap-4">
                        {user.claimedLead.email && (
                          <a
                            href={`mailto:${user.claimedLead.email}`}
                            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 underline"
                          >
                            <Mail className="w-4 h-4" />
                            <span>{user.claimedLead.email}</span>
                          </a>
                        )}
                        {user.claimedLead.phone_number && (
                          <a
                            href={`tel:${user.claimedLead.phone_number}`}
                            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 underline"
                          >
                            <Phone className="w-4 h-4" />
                            <span>
                              {formatPhone(user.claimedLead.phone_number)}
                            </span>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex-shrink-0 mt-2 md:mt-0 md:ml-auto flex flex-col gap-2">
                    <a
                      href={`mailto:${user.claimedLead.email}?subject=Question about my real estate search`}
                      className="px-5 py-2.5 rounded-lg border bg-transparent text-sm font-medium flex items-center justify-center gap-2"
                      style={{
                        borderColor: config?.branding?.colors?.primary,
                        color: config?.branding?.colors?.primary,
                      }}
                    >
                      <Mail className="w-4 h-4" />
                      <span>Email Agent</span>
                    </a>
                    {user.claimedLead.phone_number && (
                      <a
                        href={`tel:${user.claimedLead.phone_number}`}
                        className="px-5 py-2.5 rounded-lg border bg-transparent text-sm font-medium flex items-center justify-center gap-2"
                        style={{
                          borderColor: config?.branding?.colors?.primary,
                          color: config?.branding?.colors?.primary,
                        }}
                      >
                        <Phone className="w-4 h-4" />
                        <span>Call Agent</span>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

        {/* Coming Soon Alert - Commented out as requested 
        <div className="mb-8 p-6 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-5"
            style={{
              backgroundImage: `radial-gradient(${
                config?.branding?.colors?.primary || "#4F46E5"
              } 1px, transparent 1px)`,
              backgroundSize: "24px 24px",
            }}
          />

          <div className="relative flex items-center gap-3">
            <div
              className="flex-shrink-0 p-2 rounded-lg"
              style={{
                backgroundColor: `${config?.branding?.colors?.primary}15`,
              }}
            >
              <Rocket
                className="w-6 h-6"
                style={{ color: config?.branding?.colors?.primary }}
              />
            </div>
            <div>
              <h3
                className="text-sm lg:text-lg font-semibold mb-1"
                style={{ color: config?.branding?.colors?.primary }}
              >
                Exciting Updates Coming!
              </h3>
              <p className="text-gray-600 text-sm lg:text-base">
                Stay tuned for new features and improvements to enhance your
                experience.
              </p>
            </div>
          </div>
        </div>
        */}

        {/* Saved Properties Section */}
        <div className="mb-10 p-6 rounded-xl bg-gray-50 border border-gray-200 shadow-md">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-md lg:text-xl font-semibold">
              Saved Properties
            </h2>
            <button
              onClick={() =>
                queryClient.invalidateQueries({ queryKey: ["savedProperties"] })
              }
              className="cursor-pointer bg-transparent p-0"
            >
              <RefreshCw
                className={`w-4 h-4 lg:w-5 lg:h-5 text-gray-500 ${
                  isRefetching ? "animate-spin" : ""
                }`}
              />
            </button>
          </div>
          <SavedPropertiesGrid />
        </div>

        {/* Saved Search Preferences Section - Commented out as requested
        <div>
          <SavedSearchSection
            primaryColor={config?.branding?.colors?.primary}
          />
        </div>
        */}
      </div>
    </div>
  );
};

export default DashboardPage;
